/** Fetch and cache scenario content (player or full view) and per-node reveals. */
import type { Scenario, ScenarioOrigin, ScenarioStatus, ScenarioSummary, Visibility } from "@shared/scenario";
import { useEffect, useState } from "preact/hooks";
import { get, post } from "./api";

/** Player-view node shapes (answers stripped). Facilitators receive the full Scenario. */
export interface PlayerNarrative {
  type: "narrative";
  title: string;
  body: string;
  artifact: {
    kind: string;
    from?: string;
    to?: string;
    subject?: string;
    body: string;
    timestamp?: string;
  } | null;
}
export interface PlayerCall {
  type: "call";
  title: string;
  caller: { name: string; displayNumber: string };
  callee: { name: string; roleId: string | null };
  transcript: {
    speaker: "caller" | "callee" | "system";
    name: string | null;
    text: string;
    pauseMs: number | null;
  }[];
}
export interface PlayerDecision {
  type: "decision";
  title: string;
  prompt: string;
  context: string | null;
  timerSec: number;
  voters: "all" | string[];
  options: { id: string; label: string; detail: string | null }[];
}
export interface PlayerPoll {
  type: "poll";
  title: string;
  prompt: string;
  timerSec: number;
  options: { id: string; label: string }[];
}
export interface PlayerReveal {
  type: "reveal";
  title: string;
  body: string;
  lessons: string[];
}
export interface PlayerBranch {
  type: "branch";
  title: string;
}
export interface PlayerDebrief {
  type: "debrief";
  title: string;
  body: string;
}
export type PlayerNode =
  | PlayerNarrative
  | PlayerCall
  | PlayerDecision
  | PlayerPoll
  | PlayerReveal
  | PlayerBranch
  | PlayerDebrief;

export interface PlayerScenario {
  id: string;
  version: string;
  title: string;
  tagline: string;
  summary: string;
  difficulty: Scenario["difficulty"];
  estimatedMinutes: number;
  audience: Scenario["audience"];
  audienceNote?: string;
  basedOn: Scenario["basedOn"];
  tags: string[];
  learningObjectives: string[];
  dimensions: Scenario["dimensions"];
  roles: Scenario["roles"];
  start: string;
  nodes: Record<string, PlayerNode>;
  injects: { id: string; title: string }[];
  badges: Scenario["badges"];
}

export interface ScenarioResponse<T> {
  locale: string;
  coverage: number;
  view: "player" | "full";
  scenario: T;
}

export interface RevealDecision {
  type: "decision";
  options: {
    id: string;
    label: string;
    quality: "best" | "good" | "risky" | "bad";
    feedback: string;
    effects: { vars: Record<string, number>; score: Record<string, number> };
    tags: string[];
  }[];
}
export interface RevealCall {
  type: "call";
  caller: { name: string; displayNumber: string; spoofed: boolean; voice?: string };
  redFlags: string[];
  tells: { index: number; tell: string | null }[];
}
export type Reveal = RevealDecision | RevealCall | { type: string };

const scenarioCache = new Map<string, Promise<ScenarioResponse<PlayerScenario | Scenario>>>();

/**
 * `code` names the session whose copy of the scenario to read: the room snapshots the scenario it
 * was created with, so a participant sees what the room runs even if the owner's draft has moved on.
 */
export function fetchScenario<T = PlayerScenario>(
  id: string,
  locale: string,
  view: "player" | "full",
  code?: string | null,
): Promise<ScenarioResponse<T>> {
  const key = `${id}|${locale}|${view}|${code ?? ""}`;
  let p = scenarioCache.get(key);
  if (!p) {
    p = get<ScenarioResponse<PlayerScenario | Scenario>>(
      `/api/scenarios/${encodeURIComponent(id)}?lang=${encodeURIComponent(locale)}&view=${view}${
        code ? `&code=${encodeURIComponent(code)}` : ""
      }`,
    );
    scenarioCache.set(key, p);
    p.catch(() => scenarioCache.delete(key));
  }
  return p as Promise<ScenarioResponse<T>>;
}

export function useScenario<T = PlayerScenario>(
  id: string | null,
  locale: string,
  view: "player" | "full",
  code?: string | null,
) {
  const [data, setData] = useState<ScenarioResponse<T> | null>(null);
  const [error, setError] = useState<Error | null>(null);
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setError(null);
    fetchScenario<T>(id, locale, view, code)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e: Error) => {
        if (!cancelled) setError(e);
      });
    return () => {
      cancelled = true;
    };
  }, [id, locale, view, code]);
  return { data, error };
}

/** A catalogue entry as `GET /api/scenarios` lists it; custom scenarios carry their record too. */
export interface CatalogueEntry extends ScenarioSummary {
  origin: ScenarioOrigin;
  visibility?: Visibility;
  status?: ScenarioStatus;
  publishedVersion?: string | null;
  unpublishedChanges?: boolean;
  sourceId?: string | null;
  sourceTitle?: string | null;
  approved?: boolean;
  updatedAt?: number;
}

export function listScenarios(locale: string): Promise<{ locale: string; scenarios: CatalogueEntry[] }> {
  return get(`/api/scenarios?lang=${encodeURIComponent(locale)}`);
}

/** Copy a scenario into the caller's own drafts; resolves with the new entry. */
export function forkScenario(id: string, title?: string): Promise<{ scenario: CatalogueEntry }> {
  return post(`/api/scenarios/${encodeURIComponent(id)}/fork`, title ? { title } : {});
}

const revealCache = new Map<string, Promise<Reveal>>();

export function fetchReveal(code: string, nodeId: string, token: string, locale: string): Promise<Reveal> {
  const key = `${code}|${nodeId}|${locale}`;
  let p = revealCache.get(key);
  if (!p) {
    p = get<{ reveal: Reveal }>(
      `/api/join/${code}/reveal/${encodeURIComponent(nodeId)}?token=${encodeURIComponent(token)}&lang=${encodeURIComponent(locale)}`,
    ).then((r) => r.reveal);
    revealCache.set(key, p);
    p.catch(() => revealCache.delete(key));
  }
  return p;
}

export function useReveal(
  code: string | null,
  nodeId: string | null,
  token: string | null,
  locale: string,
  enabled: boolean,
) {
  const [reveal, setReveal] = useState<Reveal | null>(null);
  useEffect(() => {
    setReveal(null);
    if (!enabled || !code || !nodeId || !token) return;
    let cancelled = false;
    fetchReveal(code, nodeId, token, locale)
      .then((r) => {
        if (!cancelled) setReveal(r);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [code, nodeId, token, locale, enabled]);
  return reveal;
}
